import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface TomadorData {
	tomadorNombre: string
	tomadorCuit: string
	tomadorDomicilio: string
	tomadorTelefono: string
	tomadorEmail: string
}

interface CaucionGeneralTomadorScreenProps {
	onNext: (data: TomadorData) => void
	onBack: () => void
	data?: Partial<TomadorData>
}

export function CaucionGeneralTomadorScreen({
	onNext,
	onBack,
	data,
}: CaucionGeneralTomadorScreenProps) {
	const [formData, setFormData] = useState<TomadorData>({
		tomadorNombre: data?.tomadorNombre || '',
		tomadorCuit: data?.tomadorCuit || '',
		tomadorDomicilio: data?.tomadorDomicilio || '',
		tomadorTelefono: data?.tomadorTelefono || '',
		tomadorEmail: data?.tomadorEmail || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateCUIT = (cuit: string) => {
		const cleaned = cuit.replace(/-/g, '')
		return cleaned.length === 11 && /^\d+$/.test(cleaned)
	}

	const formatCUIT = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (cleaned.length <= 2) return cleaned
		if (cleaned.length <= 10)
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
		return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10, 11)}`
	}

	const validateEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.tomadorNombre.trim()) {
			newErrors.tomadorNombre = 'Ingresa el nombre del tomador'
		}
		if (!formData.tomadorCuit.trim()) {
			newErrors.tomadorCuit = 'Ingresa el CUIT'
		} else if (!validateCUIT(formData.tomadorCuit)) {
			newErrors.tomadorCuit = 'El CUIT debe tener 11 dígitos (XX-XXXXXXXX-X)'
		}
		if (!formData.tomadorDomicilio.trim()) {
			newErrors.tomadorDomicilio = 'Ingresa el domicilio'
		}
		if (!formData.tomadorTelefono.trim()) {
			newErrors.tomadorTelefono = 'Ingresa el teléfono'
		}
		if (!formData.tomadorEmail.trim()) {
			newErrors.tomadorEmail = 'Ingresa el email'
		} else if (!validateEmail(formData.tomadorEmail)) {
			newErrors.tomadorEmail = 'Email inválido'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.tomadorNombre.trim() &&
		formData.tomadorCuit.trim() &&
		formData.tomadorDomicilio.trim() &&
		formData.tomadorTelefono.trim() &&
		formData.tomadorEmail.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-8">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos del Tomador
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Nombre / Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre de la empresa"
							value={formData.tomadorNombre}
							onChange={(e) => {
								setFormData({ ...formData, tomadorNombre: e.target.value })
								setErrors({ ...errors, tomadorNombre: '' })
							}}
							errorText={errors.tomadorNombre}
							required
						/>
						<InputText
							label="CUIT"
							labelPosition="internal"
							size="l"
							placeholder="XX-XXXXXXXX-X"
							value={formData.tomadorCuit}
							onChange={(e) => {
								setFormData({
									...formData,
									tomadorCuit: formatCUIT(e.target.value),
								})
								setErrors({ ...errors, tomadorCuit: '' })
							}}
							errorText={errors.tomadorCuit}
							required
						/>
						<InputText
							label="Domicilio"
							labelPosition="internal"
							size="l"
							placeholder="Dirección completa"
							value={formData.tomadorDomicilio}
							onChange={(e) => {
								setFormData({ ...formData, tomadorDomicilio: e.target.value })
								setErrors({ ...errors, tomadorDomicilio: '' })
							}}
							errorText={errors.tomadorDomicilio}
							required
						/>
						<InputText
							label="Teléfono"
							labelPosition="internal"
							size="l"
							placeholder="Teléfono de contacto"
							value={formData.tomadorTelefono}
							onChange={(e) => {
								setFormData({ ...formData, tomadorTelefono: e.target.value })
								setErrors({ ...errors, tomadorTelefono: '' })
							}}
							errorText={errors.tomadorTelefono}
							required
						/>
						<InputText
							label="Email"
							labelPosition="internal"
							size="l"
							placeholder="correo@ejemplo.com"
							type="email"
							value={formData.tomadorEmail}
							onChange={(e) => {
								setFormData({ ...formData, tomadorEmail: e.target.value })
								setErrors({ ...errors, tomadorEmail: '' })
							}}
							errorText={errors.tomadorEmail}
							required
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
